<template>
    <tbody :class="{ 'p-datatable-tbody': renderedEntriesEmpty }"
           role="rowgroup">
    <tr>
        <td colspan="999">
            <div v-if="cardsContainerDisplayed"
                 ref="cardsContainer"
                 class="cards-container">
                <table-card v-for="entry in displayedEntries"
                            :key="entry.id"
                            :dropdownMenuOpen="openMenuId === entry.id"
                            :entry="entry"
                            :getEditLink="getEditLink"
                            :menuDropdownItems="menuDropdownItems"
                            :selected="!!selectedRowIdsMap[entry.id]"
                            @selected="$emit('selected', entry.id)"
                            @unselected="$emit('unselected', entry.id)"
                            @edit-button-click="$emit('edit-button-click', entry.id)"
                            @menu-button-click="$emit('menu-button-click', entry.id)"
                            @thumbnailPreviewClick="$emit('thumbnailPreviewClick', $event)"
                />
                <div v-for="virtualCard in requiredVirtualCards"
                     :key="virtualCard"
                     :style="virtualCardStyle" />
            </div>
            <template v-else-if="loading">
                <slot name="loading" />
            </template>
            <template v-else>
                <slot name="empty" />
            </template>
        </td>
    </tr>
    </tbody>
</template>

<script>
import { defineComponent } from 'vue'
import TableCard from './table-card.vue'
import { nextTick } from 'vue'

export default defineComponent({
    components: {
        TableCard
    },
    props: {
        loading: {
            type: Boolean,
            required: true
        },
        renderedEntries: {
            type: [Array, null],
            required: true
        },
        selectedEntries: {
            type: [Array, null],
            required: true
        },
        selectedRowIdsMap: {
            type: Object,
            required: true
        },
        openMenuId: {
            type: [Number, null]
        },
        menuDropdownItems: {
            type: Array,
            required: true
        },
        firstDisplayedEntryIndex: {
            type: Number,
            required: true
        },
        numRowsPerPage: {
            type: Number,
            required: true
        },
        cardInlineSize: {
            type: Number,
            default: 200
        },
        gapSize: {
            type: Number,
            default: 12
        },
        getEditLink: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            resizeObserver: null,

            numActuallyFittingCards: 0,
            requiredVirtualCards: 0
        }
    },
    computed: {
        renderedEntriesEmpty() {
            return this.renderedEntries && this.renderedEntries.length === 0
        },
        displayedEntries() {
            return this.renderedEntries ? this.renderedEntries.slice(this.firstDisplayedEntryIndex, this.firstDisplayedEntryIndex + this.numRowsPerPage) : []
        },
        cardsContainerDisplayed() {
            return !this.loading && !this.renderedEntriesEmpty
        },
        virtualCardStyle() {
            return { width: `${this.cardInlineSize}px` }
        }
    },
    methods: {
        destroyResizeObserver() {
            if (this.resizeObserver) this.resizeObserver.disconnect()
            this.resizeObserver = null
        }
    },
    beforeDestroy() {
        this.destroyResizeObserver()
    },
    watch: {
        cardsContainerDisplayed: {
            immediate: true,
            async handler(val) {
                this.destroyResizeObserver()

                if (val) {
                    // Await 1 tick for the cardsContainer to be mounted
                    await nextTick()

                    this.resizeObserver = new ResizeObserver((entries) => {
                        for (const entry of entries) {

                            if (!entry.contentBoxSize) return

                            const inlineSize = entry.contentBoxSize[0].inlineSize

                            const numPossiblyFittingCards = Math.floor(inlineSize / this.cardInlineSize)

                            const numPossibleGaps = numPossiblyFittingCards - 1

                            const gapRemainderSize = inlineSize % this.cardInlineSize

                            const gapTest = gapRemainderSize / (numPossibleGaps * this.gapSize)

                            let numActuallyFittingCards = numPossiblyFittingCards
                            if (gapTest < 1) {
                                // numPossiblyFittingCards doesn't fit, not enough space for gaps.
                                numActuallyFittingCards -= 1
                            }

                            const numCardsInLastLine = this.displayedEntries.length % numActuallyFittingCards
                            this.requiredVirtualCards = numCardsInLastLine > 0 ? numActuallyFittingCards - numCardsInLastLine : 0
                            this.numActuallyFittingCards = numActuallyFittingCards
                        }
                    })

                    this.resizeObserver.observe(this.$refs.cardsContainer)
                }
            }
        }
    }
})
</script>

<style>
.cards-container {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px;
    justify-content: space-around;
}
</style>
